import React, { useState } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getQueryFn, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface RetailerLabel {
  id: number;
  name: string;
  description?: string | null;
  retailerId: number;
  createdAt: string;
  updatedAt: string;
}

export default function RetailerLabelsPage({ retailerIdOverride }: { retailerIdOverride?: number } = {}) {
  const params = useParams() as { retailerId?: string };
  const retailerIdNum = retailerIdOverride ?? (params?.retailerId ? Number(params.retailerId) : NaN);
  const retailerId = String(retailerIdNum);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<RetailerLabel | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const listQuery = useQuery<RetailerLabel[] | null>({
    queryKey: [`/api/retailers/${retailerId}/labels`],
    queryFn: getQueryFn({ on401: 'returnNull' }),
    enabled: Number.isFinite(retailerIdNum),
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
      const body: any = { name: payload.name };
      if (payload.description?.trim()) body.description = payload.description.trim();
      await apiRequest(`/api/retailers/${retailerIdNum}/labels`, 'POST', body);
    },
    onSuccess: () => {
      setName('');
      setDescription('');
      setIsCreateDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: [`/api/retailers/${retailerId}/labels`] });
      toast({
        title: "Success",
        description: "Label created successfully",
      });
    },
    onError: (error: any) => {
      const errorData = error.data || {};
      const message = errorData.error || errorData.detail || error.message || "Failed to create label";
      console.error('[LABELS_CREATE] Error:', error, errorData);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
        duration: 8000,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { labelId: number; name: string; description?: string }) => {
      const body: any = { name: payload.name };
      if (payload.description !== undefined) {
        body.description = payload.description?.trim() || null;
      }
      await apiRequest(`/api/retailers/${retailerIdNum}/labels/${payload.labelId}`, 'PATCH', body);
    },
    onSuccess: () => {
      setEditingLabel(null);
      setEditName('');
      setEditDescription('');
      queryClient.invalidateQueries({ queryKey: [`/api/retailers/${retailerId}/labels`] });
      toast({
        title: "Success",
        description: "Label updated successfully",
      });
    },
    onError: (error: any) => {
      const errorData = error.data || {};
      const message = errorData.error || error.message || "Failed to update label";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(`/api/retailers/${retailerIdNum}/labels/${id}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/retailers/${retailerId}/labels`] });
      toast({
        title: "Success",
        description: "Label deleted successfully",
      });
    },
    onError: (error: any) => {
      const errorData = error.data || {};
      const message = errorData.error || error.message || "Failed to delete label";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const labels = listQuery.data || [];

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({ name: name.trim(), description });
  };

  const handleEditClick = (label: RetailerLabel) => {
    setEditingLabel(label);
    setEditName(label.name);
    setEditDescription(label.description || '');
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLabel || !editName.trim()) return;
    updateMutation.mutate({
      labelId: editingLabel.id,
      name: editName.trim(),
      description: editDescription,
    });
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this label?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Labels</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Labels are optional groupings used for organising site lists (e.g. Branded Bays, Front of Store).
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Label
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Label</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Label name (required)"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description (optional)"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Creating…' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {listQuery.isLoading ? (
        <div>Loading…</div>
      ) : listQuery.isError ? (
        <div className="text-red-600">Error loading labels</div>
      ) : !labels || labels.length === 0 ? (
        <div className="text-gray-600">No labels defined for this retailer yet.</div>
      ) : (
        <div className="overflow-auto">
          <table className="min-w-[500px] w-full border border-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 border-b">Name</th>
                <th className="text-left px-3 py-2 border-b">Description</th>
                <th className="text-left px-3 py-2 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {labels.map((label) => (
                <tr key={label.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 border-b">{label.name}</td>
                  <td className="px-3 py-2 border-b text-gray-700">{label.description || '-'}</td>
                  <td className="px-3 py-2 border-b">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(label)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(label.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editingLabel !== null} onOpenChange={(open) => !open && setEditingLabel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Label</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="editName">Name *</Label>
              <Input
                id="editName"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Label name (required)"
                required
              />
            </div>
            <div>
              <Label htmlFor="editDescription">Description</Label>
              <Textarea
                id="editDescription"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Description (optional)"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingLabel(null);
                  setEditName('');
                  setEditDescription('');
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Updating…' : 'Update'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
